import React, { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../SEO';

const TermsConditions: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pt-28">
            <SEO
                title="Terms & Conditions"
                description="Rules and regulations for using the NexOverseas platform and services."
                canonical="/terms-conditions"
            />
            <div className="max-w-4xl mx-auto px-6 md:px-8 py-16">
                {/* Back Link */}
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-[#009B8F] hover:text-[#007d72] transition-colors mb-8 font-medium"
                >
                    <ArrowLeft size={18} />
                    Back to Home
                </Link>

                {/* Header */}
                <div className="mb-12">
                    <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#00151a] mb-4">
                        Terms & Conditions
                    </h1>
                    <p className="text-slate-500 text-sm">Last updated: 1st March 2026</p>
                </div>

                {/* Content */}
                <div className="prose prose-slate max-w-none">
                    <div className="space-y-6 text-slate-700 leading-relaxed">
                        <p>
                            These Terms & Conditions explain how Nex Overseas Ltd provides services through our website and platform. By creating an account or using our services, you agree to these Terms. If you do not agree, you should not use our website.
                        </p>

                        <h2 className="text-2xl font-serif font-bold text-[#00151a] mt-10 mb-4">Our Services</h2>
                        <p>
                            Our platform offers education consultancy, coaching and training, accommodation support, travel assistance, and a solicitor‑connection service for immigration or legal matters. We do not provide immigration advice ourselves. Any legal or immigration‑related guidance is provided directly by the solicitor or firm you choose to work with. Our role is limited to connecting you with them if you request it.
                        </p>

                        <h2 className="text-2xl font-serif font-bold text-[#00151a] mt-10 mb-4">Account Responsibilities</h2>
                        <p>
                            To use our services, you must create an account and provide accurate information. You are responsible for keeping your login details secure. You must not share your account with others or use our platform for unlawful purposes. We reserve the right to suspend or close accounts that violate our Terms or misuse our services.
                        </p>

                        <h2 className="text-2xl font-serif font-bold text-[#00151a] mt-10 mb-4">Payments & Pricing</h2>
                        <p>
                            Some parts of our platform are free, including the initial consultation. All other services are paid. Prices may change from time to time, but any change will not affect purchases already completed. Payments are processed securely through trusted third‑party providers such as Stripe or PayPal. We do not store your card details.
                        </p>

                        <h2 className="text-2xl font-serif font-bold text-[#00151a] mt-10 mb-4">Service Delivery</h2>
                        <p>
                            Our services are delivered digitally. Once you access or use a paid service, it becomes non‑refundable unless a technical issue on our side prevents you from accessing what you purchased. More details are provided in our <Link to="/refund-policy" className="text-[#009B8F] hover:underline">Refund Policy</Link>. We are not responsible for delays or outcomes related to universities, accommodation providers, travel companies, or solicitor firms, as these are independent third parties.
                        </p>

                        <h2 className="text-2xl font-serif font-bold text-[#00151a] mt-10 mb-4">Solicitor Connection Service</h2>
                        <p>
                            If you request to be connected with a solicitor, we will only share your information with your permission. We are not responsible for the advice, decisions, or actions of any solicitor or legal firm. Any agreement you enter into with a solicitor is strictly between you and them.
                        </p>

                        <h2 className="text-2xl font-serif font-bold text-[#00151a] mt-10 mb-4">Intellectual Property</h2>
                        <p>
                            All content on our website, including text, graphics, branding, and digital tools, belongs to us and is protected by copyright and intellectual property laws. You may not copy, distribute, or use our content without written permission.
                        </p>

                        <h2 className="text-2xl font-serif font-bold text-[#00151a] mt-10 mb-4">Updates to Terms</h2>
                        <p>
                            We may update these Terms from time to time. If changes are significant, we will notify you on our website or by email. By continuing to use our services after updates, you accept the revised Terms.
                        </p>

                        <h2 className="text-2xl font-serif font-bold text-[#00151a] mt-10 mb-4">Contact Us</h2>
                        <p>
                            If you have questions about these Terms & Conditions, you can contact us at <a href="mailto:info@nexoverseas.co.uk" className="text-[#009B8F] hover:underline">info@nexoverseas.co.uk</a>.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TermsConditions;
